import * as ort from 'onnxruntime-web/webgpu';
import { GpuContext, type GpuContextOptions } from '@design-ai/gpu-core';
import { buildScaleModel } from './tiny-model.js';

export interface RuntimeOptions extends GpuContextOptions {
  /**
   * Where ORT's `.wasm` / `.mjs` artifacts live: either a URL prefix, or
   * explicit URLs for each file.
   *
   * The app passes bundler-resolved URLs (see `apps/web/src/ort-assets.ts`) so
   * the runtime is served from the app's own origin rather than a CDN, and can
   * never drift out of sync with the bundled JS.
   */
  wasmPaths?: string | { wasm?: string; mjs?: string };
  /** WASM worker threads used by any CPU fallback. 0 lets ORT decide. */
  numThreads?: number;
  logLevel?: 'verbose' | 'info' | 'warning' | 'error' | 'fatal';
}

let sharedContext: GpuContext | null = null;
let deviceIsShared = false;
/**
 * A session held open for the lifetime of the runtime.
 *
 * ORT destroys its `GPUDevice` when the last session is released, so without
 * this the shared device dies the moment the user swaps models - and every
 * texture in the workbench dies with it.
 */
let keepAlive: ort.InferenceSession | null = null;

/**
 * Brings up the GPU device that ONNX Runtime and our compute kernels both use.
 *
 * A single shared `GPUDevice` is what makes the zero-copy design possible: a
 * `GPUBuffer` our shader filled can be wrapped as a model input, and the model's
 * output buffer is read straight back by the next shader. Buffers are bound to
 * the device that created them, so getting this wrong is not a slow path - it is
 * a validation error at the first inference.
 *
 * **ORT creates the device, we adopt it.** The reverse (create our own and
 * assign `env.webgpu.device`) is documented and typed, but ORT 1.27's WebGPU
 * execution provider ignores it and creates its own device anyway - which
 * surfaces only at run time as "[Buffer] is associated with [Device X], and
 * cannot be used with [Device]".
 *
 * ORT will not hand over a device before a session exists either: pre-session,
 * `env.webgpu.device` resolves to `undefined`. So we create a keep-alive session
 * from a few hundred bytes of generated ONNX, read the device out, and never
 * release that session - because releasing the last session destroys the device.
 *
 * If none of that yields a device, we fall back to our own and record that the
 * two are not shared, so `OnnxModel` stages tensors through CPU memory instead
 * of failing.
 */
export async function initRuntime(options: RuntimeOptions = {}): Promise<GpuContext> {
  if (sharedContext && !sharedContext.lost) return sharedContext;

  if (options.wasmPaths) ort.env.wasm.wasmPaths = options.wasmPaths;
  if (options.numThreads !== undefined) ort.env.wasm.numThreads = options.numThreads;
  if (options.logLevel) ort.env.logLevel = options.logLevel;

  let device = await ortDevice();
  if (!device) {
    keepAlive ??= await ort.InferenceSession.create(buildScaleModel({ height: 8, width: 8 }), {
      executionProviders: ['webgpu'],
    });
    device = await ortDevice();
  }

  deviceIsShared = Boolean(device);
  sharedContext = await GpuContext.create(device ? { ...options, device } : options);
  if (!deviceIsShared) {
    console.warn(
      '[inference] running on a separate device from ONNX Runtime; tensors will be staged through CPU memory.',
    );
  }
  return sharedContext;
}

async function ortDevice(): Promise<GPUDevice | null> {
  try {
    const device = (await ort.env.webgpu.device) as GPUDevice | undefined;
    return device ?? null;
  } catch {
    // Older builds throw rather than resolving to undefined.
    return null;
  }
}

/**
 * Whether our context and ONNX Runtime share a device.
 *
 * `false` means tensor handoff has to go through CPU memory - correct, but much
 * slower, and worth surfacing in a diagnostics panel.
 */
export function isDeviceShared(): boolean {
  return deviceIsShared;
}

/** The shared context, if `initRuntime` has run. */
export function getRuntimeContext(): GpuContext | null {
  return sharedContext;
}

/** Drops the shared context (tests, and device-lost recovery). */
export function resetRuntime(): void {
  sharedContext?.destroy();
  sharedContext = null;
  deviceIsShared = false;
  void keepAlive?.release();
  keepAlive = null;
}

/** Version strings for the about panel / bug reports. */
export function runtimeVersions(): { ort: string; ortCommon: string } {
  return { ort: ort.env.versions.web ?? 'unknown', ortCommon: ort.env.versions.common };
}

export { ort };
